import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

// WP-STANDARD | OWNER COMMITMENT CONTROL DIALOG
// Status: PATCH-2 · USES-EXISTING-AUTHORITY · NO-SCHEMA-CHANGE · SYNTAX-REVIEWED · v1
// Contract: edits the canonical OwnerCommitment.work state and optional schedule only.
// No parallel task status is introduced. waiting/blocked history timestamps continue to be
// normalized by the backend ownerCommitmentWork service.

function idOf(value) { return String(value?.id || value?._id || value || ""); }
function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 16);
}
function toIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export default function OwnerCommitmentControlDialog({ row, open, onClose, onSaved }) {
  const [state, setState] = React.useState("ready");
  const [waitingFor, setWaitingFor] = React.useState("");
  const [blockedReason, setBlockedReason] = React.useState("");
  const [unblockAction, setUnblockAction] = React.useState("");
  const [reviewAt, setReviewAt] = React.useState("");
  const [scheduledStartAt, setScheduledStartAt] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !row) return;
    const nextState = ["waiting", "blocked", "in_progress"].includes(String(row?.work?.state)) ? String(row.work.state) : "ready";
    setState(nextState === "in_progress" ? "ready" : nextState);
    setWaitingFor(String(row?.work?.waitingFor || ""));
    setBlockedReason(String(row?.work?.blockedReason || row?.work?.blockedBy || ""));
    setUnblockAction(String(row?.work?.unblockAction || ""));
    setReviewAt(toLocalInput(row?.work?.reviewAt));
    setScheduledStartAt(toLocalInput(row?.scheduledStartAt));
    setError("");
  }, [open, row]);

  const save = async () => {
    if (!row || saving) return;
    if (state === "waiting" && !waitingFor.trim()) { setError("Decime qué o a quién estás esperando."); return; }
    if (state === "blocked" && !blockedReason.trim()) { setError("Decime qué está bloqueando esta acción."); return; }
    setSaving(true); setError("");
    try {
      const work = { state };
      if (state === "waiting") {
        work.waitingFor = waitingFor.trim();
        work.reviewAt = toIso(reviewAt);
      } else if (state === "blocked") {
        work.blockedReason = blockedReason.trim();
        work.unblockAction = unblockAction.trim();
        work.reviewAt = toIso(reviewAt);
      }
      await ownerApi.updateCommitment(idOf(row), {
        work,
        scheduledStartAt: state === "ready" ? toIso(scheduledStartAt) : null,
      });
      onClose?.();
      await onSaved?.();
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar el estado operativo."));
    } finally {
      setSaving(false);
    }
  };

  const isControlled = state === "waiting" || state === "blocked";
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{pb:.8}}>
      <Stack spacing={.25}>
        <Typography sx={{fontSize:"1rem",fontWeight:1000}}>Controlar próxima acción</Typography>
        <Typography sx={{fontSize:".68rem",fontWeight:720,color:"text.secondary"}} noWrap>{row?.title || "Compromiso"}</Typography>
      </Stack>
    </DialogTitle>
    <DialogContent dividers sx={{py:1.2}}>
      <Stack spacing={1.2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Box>
          <Typography sx={{fontSize:".62rem",fontWeight:1000,color:"text.secondary",mb:.55}}>¿DÓNDE ESTÁ LA PELOTA?</Typography>
          <ToggleButtonGroup exclusive fullWidth size="small" value={state} onChange={(_, value)=>value && setState(value)}>
            <ToggleButton value="ready" sx={{textTransform:"none",fontWeight:900,gap:.5}}><CheckCircleOutlineRoundedIcon fontSize="small"/>Depende de mí</ToggleButton>
            <ToggleButton value="waiting" sx={{textTransform:"none",fontWeight:900,gap:.5}}><HourglassTopRoundedIcon fontSize="small"/>Esperando</ToggleButton>
            <ToggleButton value="blocked" sx={{textTransform:"none",fontWeight:900,gap:.5}}><BlockRoundedIcon fontSize="small"/>Bloqueado</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {state === "waiting" ? <TextField label="¿Qué o a quién estás esperando?" value={waitingFor} onChange={(e)=>setWaitingFor(e.target.value)} autoFocus fullWidth size="small" placeholder="Ej. Confirmación de Agustín para arrancar Fase 3"/> : null}
        {state === "blocked" ? <>
          <TextField label="¿Qué lo bloquea?" value={blockedReason} onChange={(e)=>setBlockedReason(e.target.value)} autoFocus fullWidth size="small" placeholder="Ej. Falta acceso al dominio del cliente"/>
          <TextField label="Próxima acción para destrabarlo" value={unblockAction} onChange={(e)=>setUnblockAction(e.target.value)} fullWidth size="small" placeholder="Ej. Pedir acceso al administrador"/>
        </> : null}

        {isControlled ? <Box sx={{p:1,border:"1px solid",borderColor:"divider",borderRadius:2}}>
          <Stack direction="row" spacing={.7} alignItems="center" sx={{mb:.7}}>
            <EventRoundedIcon sx={{fontSize:18,color:"text.secondary"}}/>
            <Box sx={{minWidth:0,flex:1}}>
              <Typography sx={{fontSize:".68rem",fontWeight:950}}>Revisión opcional</Typography>
              <Typography sx={{fontSize:".6rem",fontWeight:720,color:"text.secondary"}}>Si elegís una fecha, la acción sale del foco hasta que corresponda revisarla.</Typography>
            </Box>
            <Chip size="small" label="sin inventar urgencia" variant="outlined" sx={{height:20,fontSize:".52rem",fontWeight:850}}/>
          </Stack>
          <TextField type="datetime-local" value={reviewAt} onChange={(e)=>setReviewAt(e.target.value)} fullWidth size="small" InputLabelProps={{shrink:true}} label="Volver a revisar"/>
        </Box> : <Box sx={{p:1,border:"1px solid",borderColor:"divider",borderRadius:2}}>
          <Typography sx={{fontSize:".68rem",fontWeight:950,mb:.65}}>Programar, si ya tiene momento</Typography>
          <TextField type="datetime-local" value={scheduledStartAt} onChange={(e)=>setScheduledStartAt(e.target.value)} fullWidth size="small" InputLabelProps={{shrink:true}} label="Inicio en agenda"/>
          <Typography sx={{mt:.55,fontSize:".59rem",fontWeight:720,color:"text.secondary"}}>Vacío significa ejecutable pero todavía sin hora. Google Calendar sigue siendo proyección; el compromiso continúa siendo la autoridad.</Typography>
        </Box>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={saving}>Cancelar</Button>
      <Button variant="contained" onClick={save} disabled={saving} sx={{textTransform:"none",fontWeight:950}}>{saving ? "Guardando…" : isControlled ? "Dejar controlado" : scheduledStartAt ? "Programar" : "Dejar listo"}</Button>
    </DialogActions>
  </Dialog>;
}
